import { defineStore } from 'pinia';
import { Ref } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import { useProfileStore } from './profileStore';
import { useMapStore } from './remapStore';

export type AlertType = 'info' | 'success' | 'danger' | 'warning';
type SidebarSectionNamesType = 'functions' | 'topics' | 'projects' | 'states';

type ToolbarMode = 'collapsed' | 'uncollapsed';
type SidebarMode = 'collapsed' | 'uncollapsed';
type NavbarMode = 'collapsed' | 'uncollapsed';
type PresentationMode = 'on' | 'off';

const AI_ASSISTANT_DOCK_WIDTH_DEFAULT = 460;
export const AI_ASSISTANT_DOCK_WIDTH_MIN = 320;
const AI_ASSISTANT_DOCK_WIDTH_MAX = 900;

/** One named answer of a {@link useUiStore.confirmChoice} dialog. */
export interface ConfirmChoice<T extends string = string> {
  value: T;
  text: string;
  /** UiButton variant; defaults to 'default' */
  variant?: 'default' | 'primary' | 'error' | 'flat';
  /** The choice taken on Enter */
  isDefault?: boolean;
}

interface Confirmation {
  id: string;
  title?: string;
  subtitle?: string;
  description?: string;
  yesText?: string;
  noText?: string;
  resolve: (value: boolean | string) => void;
  requiredAnswer?: string;
  kind: AlertType;
  /** Named choices instead of the yes/no pair (Cancel is always added) */
  choices?: ConfirmChoice[];
}

interface Alert {
  id: string;
  type: AlertType;
  message: string;
}

interface Breadcrumb {
  id: string;
  caption: string | Ref;
  to: RouteLocationRaw | Ref;
}

type KeymapMap = Record<string, () => void>;
type KeymapObj = { id: string; map: KeymapMap };

interface PendingOperation {
  accept: () => Promise<void>;
  reject?: () => void;
}

export type UiStoreState = {
  breadcrumbs: Breadcrumb[];
  confirmations: Confirmation[];
  isContextFormLoading: boolean;
  isInitializedKeymaps: boolean;
  pendingOperation?: PendingOperation;
  unlockFn?: (value: 0 | 1 | 2) => void;
  keymaps: KeymapObj[];
  alerts: Alert[];
  toolbarMode: ToolbarMode;
  sidebarMode: SidebarMode;
  navbarMode: NavbarMode;
  presentationMode: PresentationMode;
  projectActivityTab: string;
  templateActivityTab: string;
  projectActivityTabOrder: string[];
  templateActivityTabOrder: string[];
  projectMultiActivityTab: string;
  templateMultiActivityTab: string;
  projectMultiActivityTabOrder: string[];
  templateMultiActivityTabOrder: string[];
  // Phase/milestone context-bar tab, runtime-only, so quality navigation can
  // steer these bars.
  templatePhaseTab: string;
  templateMilestoneTab: string;
  aiAssistantOpen: boolean;
  // Runtime-only: the Sparkles button opened the AI panel, which takes the
  // focus when it mounts.
  aiAssistantFocusRequested: boolean;
  aiAssistantDockWidth: number;
  templateQualityPanelOpen: boolean;
};

interface ConfirmOptions {
  kind?: AlertType;
  yesText?: string;
  requiredAnswer?: string;
  noText?: string;
  subtitle?: string;
  description?: string;
}

export const useUiStore = defineStore('uiStore', {
  state: (): UiStoreState => ({
    breadcrumbs: [],
    confirmations: [],
    isContextFormLoading: false,
    isInitializedKeymaps: false,
    pendingOperation: undefined,
    unlockFn: undefined,
    keymaps: [],
    alerts: [],
    toolbarMode: 'uncollapsed',
    sidebarMode: 'uncollapsed',
    navbarMode: 'uncollapsed',
    presentationMode: 'off',
    projectActivityTab: 'details',
    templateActivityTab: 'details',
    projectActivityTabOrder: [],
    templateActivityTabOrder: [],
    projectMultiActivityTab: 'details',
    templateMultiActivityTab: 'details',
    projectMultiActivityTabOrder: [],
    templateMultiActivityTabOrder: [],
    templatePhaseTab: 'details',
    templateMilestoneTab: 'details',
    aiAssistantOpen: false,
    aiAssistantFocusRequested: false,
    aiAssistantDockWidth: AI_ASSISTANT_DOCK_WIDTH_DEFAULT,
    templateQualityPanelOpen: false,
  }),

  actions: {
    async confirmUnlockUi(): Promise<0 | 1 | 2> {
      return new Promise((resolve) => {
        this.unlockFn = async (value: 0 | 1 | 2) => {
          if (value === 1) {
            if (this.pendingOperation) {
              if (this.pendingOperation.reject) {
                this.pendingOperation.reject();
              }
              this.pendingOperation = undefined;
            }
            resolve(value);
            this.unlockFn = undefined;
          } else if (value === 2) {
            await this.pendingOperation?.accept().then(() => {
              this.pendingOperation = undefined;
              resolve(value);
              this.unlockFn = undefined;
            });
          } else {
            resolve(value);
            this.unlockFn = undefined;
          }
        };
      });
    },

    async confirm(
      title: string,
      opts?: ConfirmOptions,
    ): Promise<boolean | string> {
      const id = crypto.randomUUID();

      return new Promise((resolve) => {
        this.confirmations.push({
          id,
          title,
          subtitle: opts?.subtitle,
          description: opts?.description,
          yesText: opts?.yesText,
          requiredAnswer: opts?.requiredAnswer,
          kind: opts?.kind || 'info',
          resolve: (value: boolean | string) => {
            this.removeConfirm(id);
            resolve(value);
          },
        });
      });
    },

    /**
     * Confirm with named choices instead of yes/no — e.g. the keep/delete
     * question of a force move. Resolves with the chosen value, or
     * `false` when the user cancels (Cancel button / Escape).
     */
    async confirmChoice<T extends string>(
      title: string,
      choices: ConfirmChoice<T>[],
      opts?: Pick<ConfirmOptions, 'kind' | 'subtitle' | 'description'>,
    ): Promise<T | false> {
      const id = crypto.randomUUID();

      return new Promise((resolve) => {
        this.confirmations.push({
          id,
          title,
          subtitle: opts?.subtitle,
          description: opts?.description,
          kind: opts?.kind || 'info',
          choices,
          resolve: (value: boolean | string) => {
            this.removeConfirm(id);
            const chosen = choices.find((choice) => choice.value === value);
            resolve(chosen ? chosen.value : false);
          },
        });
      });
    },

    removeConfirm(id: string) {
      const index = this.confirmations.findIndex((keymap) => keymap.id === id);
      if (index > -1) {
        this.confirmations.splice(index, 1);
      }
    },

    alert(type: AlertType, message: string, timeout = 10 * 1000) {
      const id = crypto.randomUUID();
      const notif = { type, message, id };
      this.alerts.unshift(notif);
      setTimeout(() => this.removeAlert(id), timeout);
    },

    removeAlert(id: string) {
      const index = this.alerts.findIndex((notif) => notif.id === id);
      if (index > -1) {
        this.alerts.splice(index, 1);
      }
    },

    initializeKeyMapping() {
      if (this.isInitializedKeymaps) {
        return;
      }
      window.addEventListener('keydown', (e) => {
        if (
          !this.keymaps.length ||
          e.ctrlKey ||
          e.altKey ||
          e.shiftKey ||
          e.metaKey
        ) {
          return;
        }

        const target = e.target as HTMLElement | null;
        if (
          target &&
          (target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.isContentEditable)
        ) {
          return;
        }

        const keymap = this.keymaps[this.keymaps.length - 1];
        const fn = keymap.map[e.key];
        if (fn) {
          fn();
        }
      });
      this.isInitializedKeymaps = true;
    },

    addKeymap(
      map: KeymapMap,
      id: string = crypto.randomUUID(),
      withUnmount = true,
    ) {
      const keymap = { map, id };
      this.keymaps.push(keymap);
      if (withUnmount) {
        onBeforeUnmount(() => this.removeKeymap(id));
      }

      return id;
    },

    removeKeymap(id: string) {
      const index = this.keymaps.findIndex((keymap) => keymap.id === id);
      if (index > -1) {
        const [keymap] = this.keymaps.splice(index, 1);

        return keymap;
      }
    },

    /**
     * Adds or updates a breadcrumb in the navigation trail.
     * If a breadcrumb with the same ID exists, it will be replaced.
     * Otherwise, a new breadcrumb is added to the end of the trail.
     *
     * @param caption - The display text for the breadcrumb (can be reactive)
     * @param to - The route location for navigation (can be reactive)
     * @param id - Unique identifier for the breadcrumb (auto-generated if not provided)
     * @param autoCleanup - Whether to automatically remove the breadcrumb on component unmount
     */
    addBreadcrumb(
      caption: string | Ref,
      to: RouteLocationRaw | Ref,
      id = crypto.randomUUID() as string,
      autoCleanup = true,
    ) {
      const crumb = { caption, to, id };
      const index = this.breadcrumbs.findIndex((crumb) => crumb.id === id);
      if (index > -1) {
        this.breadcrumbs.splice(index, 1, crumb);
      } else {
        this.breadcrumbs.push(crumb);
      }

      if (autoCleanup) {
        onBeforeUnmount(() => this.removeBreadcrumb(id));
      }
    },

    /**
     * Removes a breadcrumb from the navigation trail by its ID.
     *
     * @param id - The unique identifier of the breadcrumb to remove
     */
    removeBreadcrumb(id: string) {
      const index = this.breadcrumbs.findIndex((crumb) => crumb.id === id);
      if (index > -1) {
        this.breadcrumbs.splice(index, 1);
      }
    },

    getUserConfig() {
      const userId = useProfileStore().profile.id;

      return JSON.parse(localStorage.getItem(userId) || '{}');
    },

    getUiConfig() {
      return JSON.parse(localStorage.getItem('uiSettings') || '{}');
    },

    getContextBarConfig() {
      return JSON.parse(localStorage.getItem('contextBarSettings') || '{}');
    },

    getSideBarConfig() {
      return JSON.parse(localStorage.getItem('sideBarSettings') || '{}');
    },

    setUserConfig(userConfig: string) {
      const userId = useProfileStore().profile.id;
      localStorage.setItem(userId, JSON.stringify(userConfig));
    },

    setUiConfig(uiConfig: string) {
      localStorage.setItem('uiSettings', JSON.stringify(uiConfig));
    },

    setContextBarConfig(contextBarConfig: string) {
      localStorage.setItem(
        'contextBarSettings',
        JSON.stringify(contextBarConfig),
      );
    },

    setSideBarConfig(
      section: SidebarSectionNamesType,
      settings: Record<string, unknown>,
    ) {
      const sideBarConfig = this.getSideBarConfig();
      Object.assign(sideBarConfig, { [section]: settings });

      localStorage.setItem('sideBarSettings', JSON.stringify(sideBarConfig));
    },

    setLinkAndLabelModes() {
      const mapStore = useMapStore();
      const userConfig = this.getUserConfig();
      const key = mapStore.isTemplate ? 'template' : 'project';
      mapStore.linkMode = userConfig[key]?.linkMode || 'single';
      mapStore.labelMode = userConfig[key]?.labelMode || 'auto';
      mapStore.linkMilestoneMode = userConfig[key]?.linkMilestoneMode || 'all';
      mapStore.timeLineTemplateMode =
        userConfig[key]?.timeLineTemplateMode || 'none';
      mapStore.activityGroupingMode =
        userConfig[key]?.activityGroupingMode || 'on';
    },

    setContextBarTabs() {
      const contextBarConfig = this.getContextBarConfig();
      this.projectActivityTab =
        contextBarConfig['project']?.activityTab || 'details';
      this.templateActivityTab =
        contextBarConfig['template']?.activityTab || 'details';
      this.projectActivityTabOrder =
        contextBarConfig['project']?.activityTabOrder || [];
      this.templateActivityTabOrder =
        contextBarConfig['template']?.activityTabOrder || [];
      this.projectMultiActivityTab =
        contextBarConfig['project']?.multiActivityTab || 'details';
      this.templateMultiActivityTab =
        contextBarConfig['template']?.multiActivityTab || 'details';
      this.projectMultiActivityTabOrder =
        contextBarConfig['project']?.multiActivityTabOrder || [];
      this.templateMultiActivityTabOrder =
        contextBarConfig['template']?.multiActivityTabOrder || [];
    },

    setCollapseToolbar() {
      const mapStore = useMapStore();
      const userConfig = this.getUserConfig();
      const key = mapStore.isTemplate ? 'template' : 'project';
      this.toolbarMode = userConfig[key]?.toolbarMode || 'uncollapsed';
    },

    toggleCollapseToolbar() {
      if (this.toolbarMode === 'uncollapsed') {
        this.toolbarMode = 'collapsed';
      } else {
        this.toolbarMode = 'uncollapsed';
      }
      useUiStore().saveUserConfig('toolbarMode', this.toolbarMode);
    },

    setCollapseSidebar() {
      const mapStore = useMapStore();
      const userConfig = this.getUserConfig();
      const key = mapStore.isTemplate ? 'template' : 'project';
      this.sidebarMode = userConfig[key]?.sidebarMode || 'uncollapsed';
    },

    changeContextBarTabSelection(
      tabType: 'activityTab' | 'multiActivityTab',
      mode: string,
    ) {
      const mapStore = useMapStore();
      if (!mapStore.isTemplate) {
        if (tabType === 'activityTab') {
          this.projectActivityTab = mode;
        } else if (tabType === 'multiActivityTab') {
          this.projectMultiActivityTab = mode;
        }
      }
      if (mapStore.isTemplate) {
        if (tabType === 'activityTab') {
          this.templateActivityTab = mode;
        } else if (tabType === 'multiActivityTab') {
          this.templateMultiActivityTab = mode;
        }
      }
      this.saveContextBarConfig(tabType, mode);
    },

    saveActivityTabOrder(tabOrder: string[]) {
      if (useMapStore().isTemplate) {
        this.templateActivityTabOrder = tabOrder;
      } else {
        this.projectActivityTabOrder = tabOrder;
      }
      this.saveContextBarConfig('activityTabOrder', tabOrder);
    },

    saveMultiActivityTabOrder(tabOrder: string[]) {
      if (useMapStore().isTemplate) {
        this.templateMultiActivityTabOrder = tabOrder;
      } else {
        this.projectMultiActivityTabOrder = tabOrder;
      }
      this.saveContextBarConfig('multiActivityTabOrder', tabOrder);
    },

    toggleCollapseSidebar() {
      if (this.sidebarMode === 'uncollapsed') {
        this.sidebarMode = 'collapsed';
      } else {
        this.sidebarMode = 'uncollapsed';
      }
      useUiStore().saveUserConfig('sidebarMode', this.sidebarMode);
    },

    toggleCollapseNavbar() {
      if (this.navbarMode === 'uncollapsed') {
        this.navbarMode = 'collapsed';
      } else {
        this.navbarMode = 'uncollapsed';
      }
      useUiStore().saveUiConfig('navbarMode', this.navbarMode);
    },

    togglePresentationmode() {
      if (this.presentationMode === 'off') {
        this.presentationMode = 'on';
      } else {
        this.presentationMode = 'off';
      }
      useUiStore().saveUserConfig('presentationMode', this.presentationMode);
    },

    setCollapseNavbar() {
      const uiConfig = this.getUiConfig();
      this.navbarMode = uiConfig.navbarMode || 'uncollapsed';
    },

    setPresentationMode() {
      const mapStore = useMapStore();
      const userConfig = this.getUserConfig();
      const key = mapStore.isTemplate ? 'template' : 'project';
      this.presentationMode = userConfig[key]?.presentationMode || 'off';
    },

    saveUiConfig(
      property: 'navbarMode' | 'aiAssistantDockWidth',
      mode: string | number,
    ) {
      const uiConfig = this.getUiConfig();
      uiConfig[property] = mode;
      this.setUiConfig(uiConfig);
    },

    // The AI assistant has one place, the panel docked next to the page (WI
    // #5769); only its width is the user's.
    setAIAssistantPreferences() {
      const uiConfig = this.getUiConfig();
      if ('aiAssistantWindowMode' in uiConfig) {
        delete uiConfig.aiAssistantWindowMode;
        this.setUiConfig(uiConfig);
      }
      const storedWidth = Number(uiConfig.aiAssistantDockWidth);
      this.aiAssistantDockWidth =
        Number.isFinite(storedWidth) && storedWidth > 0
          ? Math.min(
              Math.max(storedWidth, AI_ASSISTANT_DOCK_WIDTH_MIN),
              AI_ASSISTANT_DOCK_WIDTH_MAX,
            )
          : AI_ASSISTANT_DOCK_WIDTH_DEFAULT;
    },

    closeAIAssistant() {
      this.aiAssistantOpen = false;
    },

    // Opened with the Sparkles button, the panel takes the focus (WI #5769);
    // mounted again on another project/template, it leaves it to the page.
    toggleAIAssistant() {
      this.aiAssistantOpen = !this.aiAssistantOpen;
      this.aiAssistantFocusRequested = this.aiAssistantOpen;
    },

    /** Whether the panel should take the focus — once per opening. */
    takeAIAssistantFocusRequest(): boolean {
      const requested = this.aiAssistantFocusRequested;
      this.aiAssistantFocusRequested = false;

      return requested;
    },

    // Quality results panel — transient, not persisted to the UI config.
    openTemplateQualityPanel() {
      this.templateQualityPanelOpen = true;
    },

    closeTemplateQualityPanel() {
      this.templateQualityPanelOpen = false;
    },

    toggleTemplateQualityPanel() {
      this.templateQualityPanelOpen = !this.templateQualityPanelOpen;
    },

    setAIAssistantDockWidth(width: number) {
      const clamped = Math.min(
        Math.max(width, AI_ASSISTANT_DOCK_WIDTH_MIN),
        AI_ASSISTANT_DOCK_WIDTH_MAX,
      );
      this.aiAssistantDockWidth = clamped;
      this.saveUiConfig('aiAssistantDockWidth', clamped);
    },

    saveContextBarConfig(
      property:
        | 'activityTab'
        | 'multiActivityTab'
        | 'activityTabOrder'
        | 'multiActivityTabOrder',
      mode: string | string[],
    ) {
      const contextBarConfig = this.getContextBarConfig();
      const key = useMapStore().isTemplate ? 'template' : 'project';
      if (contextBarConfig[key]) {
        contextBarConfig[key][property] = mode;
      } else {
        contextBarConfig[key] = { [property]: mode };
      }
      this.setContextBarConfig(contextBarConfig);
    },

    saveUserConfig(
      property:
        | 'labelMode'
        | 'linkMode'
        | 'linkMilestoneMode'
        | 'toolbarMode'
        | 'sidebarMode'
        | 'timeLineTemplateMode'
        | 'presentationMode'
        | 'activityGroupingMode',
      mode: string,
    ) {
      const userConfig = this.getUserConfig();
      const key = useMapStore().isTemplate ? 'template' : 'project';
      if (userConfig[key]) {
        userConfig[key][property] = mode;
      } else {
        userConfig[key] = { [property]: mode };
      }
      this.setUserConfig(userConfig);
    },
  },
});
